import { type QueryClient, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { Address } from 'viem';

import { hyperliquidInfo, subscribeWhileMounted } from '@/lib/hyperliquid/client';
import { describeFillEvent } from '@/lib/hyperliquid/orders';
import type { PerpAccountMode } from '@/lib/hyperliquid/types';

import { showPerpsSuccessToast } from '@/components/Perps/PerpsSuccessToast';

import {
  accountBalances,
  crossMargin,
  perpsAccountSelector,
  perpsActiveAssetDataSelector,
  type PerpsAccountData,
  usePerpsExchangeStore,
} from '@/store/perps/exchange';
import { usePerpsOrderFormStore } from '@/store/perps/orderForm';
import { fillKey, mergeFills } from '@/store/perps/utils';

import { getPerpsMetadata, usePerpDexs } from './usePerpDexs';
import { usePerpsLocalAgent } from './usePerpsLocalAgent';
import { usePerpsWallet } from './usePerpsWallet';

export const accountModeKey = (user: Address | undefined) =>
  ['perps', 'userAbstraction', user?.toLowerCase()] as const;
export const accountModeQueryOptions = (user: Address | undefined) =>
  queryOptions({
    queryKey: accountModeKey(user),
    queryFn: ({ signal }) => {
      if (!user) throw new Error('Connect a wallet first');
      return hyperliquidInfo.userAbstraction({ user }, signal);
    },
    enabled: !!user,
    staleTime: Infinity,
  });

// Imperative reads for mutations and stream callbacks.
export const getPerpsAccountData = (user: Address | undefined) =>
  perpsAccountSelector(user)(usePerpsExchangeStore.getState());
export const getPerpsAccountMode = (queryClient: QueryClient, user: Address | undefined) =>
  user ? (queryClient.getQueryData<PerpAccountMode>(accountModeKey(user)) ?? null) : null;
export const getPerpsActiveAssetData = (user: Address | undefined, coin: string) =>
  perpsActiveAssetDataSelector(user, coin)(usePerpsExchangeStore.getState());

/** Selects from the connected wallet's live account; re-renders only when the selection changes. */
export function usePerpsAccountData<T>(select: (account: PerpsAccountData) => T) {
  const { address } = usePerpsWallet();
  return usePerpsExchangeStore((state) => select(perpsAccountSelector(address)(state)));
}

export function usePerpsAccountMode() {
  const { address } = usePerpsWallet();
  return useQuery(accountModeQueryOptions(address)).data ?? null;
}

export function usePerpsActiveAssetData() {
  const { address } = usePerpsWallet();
  const coin = usePerpsOrderFormStore((state) => state.selectedCoin);
  return usePerpsExchangeStore(perpsActiveAssetDataSelector(address, coin));
}

export function usePerpsAccountBalances() {
  const accountMode = usePerpsAccountMode();
  return usePerpsAccountData((account) => accountBalances(account, accountMode));
}

/** All-time perps PnL, the figure Hyperliquid's portfolio page shows; sampled, so polled. */
export function usePerpsAllTimePnl() {
  const { address } = usePerpsWallet();
  return useQuery({
    queryKey: ['perps', 'portfolio', address?.toLowerCase()],
    queryFn: async ({ signal }) => {
      const perpAllTime = new Map(await hyperliquidInfo.portfolio({ user: address! }, signal)).get(
        'perpAllTime',
      );
      if (!perpAllTime) throw new Error('Hyperliquid portfolio response has no perpAllTime window');
      return Number(perpAllTime.pnlHistory.at(-1)?.[1] ?? 0);
    },
    enabled: !!address,
    refetchInterval: 60_000,
  }).data;
}

export function usePerpsCrossMargin() {
  const accountMode = usePerpsAccountMode();
  const coin = usePerpsOrderFormStore((state) => state.selectedCoin);
  return usePerpsAccountData((account) => crossMargin(account, accountMode, coin));
}

export function useSelectedPerpsPosition() {
  const coin = usePerpsOrderFormStore((state) => state.selectedCoin);
  return usePerpsAccountData((account) =>
    account.positions.find((position) => position.coin === coin),
  );
}

/** Whether the selected market's position already has a TP/SL that tracks its size. */
export function useSelectedPositionHasTpsl() {
  const coin = usePerpsOrderFormStore((state) => state.selectedCoin);
  return usePerpsAccountData((account) =>
    account.openOrders.some((order) => order.coin === coin && order.isPositionTpsl),
  );
}

/** Streams exchange snapshots under their owning wallet. Mount once per page. */
export function usePerpsAccountFeed(user: Address | undefined) {
  const selectedCoin = usePerpsOrderFormStore((state) => state.selectedCoin);
  const dexs = usePerpDexs();
  const queryClient = useQueryClient();
  usePerpsLocalAgent(user);
  useQuery(accountModeQueryOptions(user));

  useEffect(() => {
    if (!user) return;
    let disposed = false;
    const cleanups = [
      subscribeWhileMounted((client, options) =>
        client.spotState(
          { user },
          (event) => {
            if (!disposed && !options.signal?.aborted)
              usePerpsExchangeStore.getState().setSpotState(user, event.spotState);
          },
          options,
        ),
      ),
      subscribeWhileMounted((client, options) =>
        client.userFills(
          { user },
          (event) => {
            if (disposed || options.signal?.aborted) return;
            const seen = new Set(getPerpsAccountData(user).fills.map(fillKey));
            const fresh = mergeFills(undefined, event.fills).filter(
              (fill) => !seen.has(fillKey(fill)),
            );
            usePerpsExchangeStore.getState().pushFills(user, event.fills);
            if (!event.isSnapshot) {
              describeFillEvent(fresh, getPerpsMetadata(queryClient).marketByCoin).forEach(
                showPerpsSuccessToast,
              );
            }
          },
          options,
        ),
      ),
    ];
    return () => {
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
      usePerpsExchangeStore.getState().removeAccount(user);
    };
  }, [user, queryClient]);

  useEffect(() => {
    if (!user || !dexs) return;
    let disposed = false;
    const shown = new Set(dexs.map((dex) => dex.name));
    const cleanups = [
      subscribeWhileMounted((client, options) =>
        client.allDexsClearinghouseState(
          { user },
          (event) => {
            if (disposed || options.signal?.aborted) return;
            usePerpsExchangeStore.getState().setAccountStates(
              user,
              event.clearinghouseStates.filter(([dex]) => shown.has(dex)),
            );
          },
          options,
        ),
      ),
      ...dexs.map((dex) =>
        subscribeWhileMounted((client, options) =>
          client.openOrders(
            { user, dex: dex.name },
            (event) => {
              if (disposed || options.signal?.aborted) return;
              usePerpsExchangeStore.getState().setOpenOrders(user, dex.name, event.orders);
            },
            options,
          ),
        ),
      ),
    ];
    return () => {
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [user, dexs]);

  useEffect(() => {
    if (!user) return;
    let disposed = false;
    const cleanup = subscribeWhileMounted((client, options) =>
      client.activeAssetData(
        { user, coin: selectedCoin },
        (event) => {
          if (!disposed && !options.signal?.aborted)
            usePerpsExchangeStore.getState().setActiveAssetData(user, selectedCoin, event);
        },
        options,
      ),
    );
    return () => {
      disposed = true;
      cleanup();
      usePerpsExchangeStore.getState().removeActiveAssetData(user, selectedCoin);
    };
  }, [user, selectedCoin]);
}
